<template>
  <vs-popup class="update-pricing-tier-popup"
            id="popup"
            :title="`MANAGE PRE-SET PRICING TIERS - ${tier_data.name}`"
            @close="open = false"
            :active.sync="open"
            :buttons-hidden="true">
    <vx-card no-shadow>

      <!-- Formulation Type Tabs for Bespoke Pricing -->
      <div v-if="isBespoke && formulationTypes.length" class="mb-6">
        <vs-tabs
          :value="active_tab_index"
          @input="onTabInput"
          class="tabs-shadow-none"
          id="pricing-tier-formulation-tabs"
        >
          <vs-tab
            v-for="(type, index) in formulationTypes"
            :key="type"
            :label="formatTypeName(type)"
            @click="switchFormulationType(type, index)"
          />
        </vs-tabs>
      </div>

      <ValidationObserver ref="form" v-slot="{ handleSubmit }">
        <form id="update-pricing-tier-form" @submit.prevent="handleSubmit(onClickUpdatePricingTier)" autocomplete="off">
        
          <div class="vx-row mb-2">
            <div class="vx-col w-2/5">
              <small class="label font-semibold">From (KG)</small>
            </div>
            <div class="vx-col w-2/5">
              <small class="label font-semibold">To (KG)</small>
            </div>
            <div class="vx-col w-1/5">

            </div>
          </div>

          <div class="mb-3 vx-row items-center" v-for="(tier, index_tier) in currentTiers" :key="`${active_formulation_type}_${index_tier}`">
            <div class="w-full vx-col md:w-2/5 md:mb-0">
              <ValidationProvider :vid="index_tier+'from'" :name="index_tier+'from'" :rules="validate_prices_tier(index_tier)" v-slot="{ errors }">
                <vs-input v-model="tier.from" name="from" class="w-full" label="" :danger="!!errors[0]" :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
              </ValidationProvider>
            </div>
            <div class="w-full vx-col md:w-2/5 md:mb-0">
              <ValidationProvider :vid="index_tier+'to'" :name="index_tier+'to'" :rules="validate_max_price_tier(index_tier)" v-slot="{ errors }">
                <vs-input v-model="tier.to" name="to" class="w-full" label="" :danger="!!errors[0]" :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
              </ValidationProvider>
            </div>
            <div class="w-full vx-col md:w-1/5 md:mb-0">
              <vs-button :disabled="currentTiers.length <= 1" class="" @click="deletePriceTier(index_tier)" size="small"
                         color="danger" type="filled" icon-pack="feather" icon="icon-x"></vs-button>
            </div>
          </div>

          <vs-button @click="addPriceTier" size="small" color="success" type="filled" icon-pack="feather" class="mt-4"
                     icon="icon-plus">Add tier
          </vs-button>

          <!-- Save & Reset Button -->
          <div class="flex flex-wrap items-center justify-end mt-8">
            <vs-button v-can="'view-pricing-tier-setting'" button="submit" class="mt-2 ml-auto">Save Changes</vs-button>
            <vs-button @click="onClickReset" class="mt-2 ml-4" type="border" color="warning">Reset</vs-button>
          </div>
        </form>
      </ValidationObserver>
    </vx-card>
  </vs-popup>
</template>

<script>
  import { RepositoryFactory } from '@/repository/RepositoryFactory'
  import SuccessMessage from '@/mixins/SuccessMessage'
  import ErrorMessages from '@/mixins/ErrorMessages'

  const PricingTierRepository = RepositoryFactory.get('pricing_tier');

  function initialState() {
    return {
      open: false,
      active_tab_index: 0,
      active_formulation_type: '',
      tier_data: {
        name: '',
        type: '',
        tiers: {}
      },
      original_data: null
    }
  }

  export default {
    name: 'UpdatePricingTierPopUp',
    data() {
      return initialState()
    },
    computed: {
      isBespoke() {
        return this.tier_data.type === 'bespoke-pricing';
      },
      formulationTypes() {
        if (!this.isBespoke || !this.tier_data.tiers || typeof this.tier_data.tiers !== 'object' || Array.isArray(this.tier_data.tiers)) {
          return [];
        }
        return Object.keys(this.tier_data.tiers);
      },
      currentTiers() {
        if (this.isBespoke) {
          if (!this.tier_data.tiers || !this.active_formulation_type) return [];
          return this.tier_data.tiers[this.active_formulation_type] || [];
        }
        return Array.isArray(this.tier_data.tiers) ? this.tier_data.tiers : [];
      }
    },
    methods: {
      async openPopup(data) {
        try {
          Object.assign(this.$data, initialState());
          if (this.$refs.form) {
            this.$refs.form.reset();
          }
          this.original_data = JSON.parse(JSON.stringify(data));
          this.tier_data = JSON.parse(JSON.stringify(data));
          if (this.isBespoke) {
            const types = Object.keys(this.tier_data.tiers || {});
            this.active_formulation_type = types.length > 0 ? types[0] : '';
            this.active_tab_index = 0;
          }
          this.open = true;
        } catch (error) {
          console.error(error);
        }
      },
      formatTypeName(type) {
        if (!type) return '';
        return type
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
      },
      switchFormulationType(type, index) {
        this.active_formulation_type = type;
        this.active_tab_index = index;
        if (this.$refs.form) {
          this.$refs.form.reset();
        }
      },
      onTabInput(index) {
        this.active_tab_index = index;
        if (this.formulationTypes[index]) {
          this.active_formulation_type = this.formulationTypes[index];
        }
        if (this.$refs.form) {
          this.$refs.form.reset();
        }
      },
      async onClickUpdatePricingTier() {
        const vue = this;
        await this.$vs.loading({
          container: '#update-pricing-tier-form',
          scale: 0.45
        });
        try {
          const ranges = this.currentTiers.map((x) => ({
            from: Number(x.from).toFixed(3),
            to: Number(x.to).toFixed(3)
          }));

          const payload = {
            type: this.tier_data.type,
            formulation_type: this.isBespoke ? this.active_formulation_type : null,
            ranges: ranges
          };

          await PricingTierRepository.updatePricingTier(payload);
          this.showSuccessMessage('Pricing tiers updated successfully!');
          await vue.$parent.refreshData();
          this.open = false;
        } catch (e) {
          this.$vs.loading.close('#update-pricing-tier-form > .con-vs-loading');
          this.convertAndNotifyError(e);
        } finally {
          this.$vs.loading.close('#update-pricing-tier-form > .con-vs-loading');
        }
      },
      validate_prices_tier(index) {
        let previousIndex = index - 1;
        return `min_than_or_equal:@${index}to|cross_over_max_equal:@${previousIndex}to|required`;
      },
      validate_max_price_tier(index) {
        return `max_than_or_equal:@${index}from|required`;
      },
      deletePriceTier(index) {
        if (this.isBespoke) {
          this.tier_data.tiers[this.active_formulation_type].splice(index, 1);
        } else {
          this.tier_data.tiers.splice(index, 1);
        }
      },
      addPriceTier() {
        if (this.isBespoke) {
          if (!this.tier_data.tiers[this.active_formulation_type]) {
            this.$set(this.tier_data.tiers, this.active_formulation_type, []);
          }
          this.tier_data.tiers[this.active_formulation_type].push({ from: '', to: '' });
        } else {
          if (!Array.isArray(this.tier_data.tiers)) {
            this.tier_data.tiers = [];
          }
          this.tier_data.tiers.push({ from: '', to: '' });
        }
      },
      onClickReset() {
        if (!this.original_data) return;
        if (this.isBespoke) {
          const originalTiers = (this.original_data.tiers && this.original_data.tiers[this.active_formulation_type])
            ? JSON.parse(JSON.stringify(this.original_data.tiers[this.active_formulation_type]))
            : [];
          this.$set(this.tier_data.tiers, this.active_formulation_type, originalTiers);
        } else {
          this.tier_data.tiers = JSON.parse(JSON.stringify(this.original_data.tiers || []));
        }
        if (this.$refs.form) {
          this.$refs.form.reset();
        }
      }
    },
    mixins: [SuccessMessage, ErrorMessages],
  }
</script>

<style scoped>
  .update-pricing-tier-popup >>> .vs-popup {
    min-width: 40% !important;
  }
</style>

